import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ZodSchema } from 'zod';

export class ApiClient {
  private readonly instance: AxiosInstance;
  private readonly ACCESS_TOKEN_KEY = 'raesApiAccessToken';
  private readonly REFRESH_TOKEN_KEY = 'raesApiRefreshToken';
  private readonly BASE_URL: string;
  private readonly STAGE: string;

  constructor(baseURL: string, stage: string) {
    this.BASE_URL = baseURL;
    this.STAGE = stage;
    this.instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  public setupInterceptors(otherAccessToken?: string | null, otherRefreshToken?: string | null) {
    // Request interceptor for adding auth token
    this.instance.interceptors.request.use((config) => {
      const token = otherAccessToken ?? localStorage.getItem(this.ACCESS_TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for token refresh
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            await this.refreshAccessToken(otherRefreshToken);
            return this.instance(originalRequest);
          } catch (refreshError) {
            return Promise.reject(new Error((refreshError as Error)?.message || 'Token refresh failed'));
          }
        }
        return Promise.reject(new Error(error?.message || 'Request failed'));
      }
    );
  }

  private async refreshAccessToken(otherRefreshToken?: string | null) {
    const refreshToken = otherRefreshToken ?? localStorage.getItem(this.REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const apiUrl = new URL(this.BASE_URL).origin;

    const response = await this.instance.post(`${apiUrl}/${this.STAGE}/authentication-${this.STAGE}/refresh`, {
      refresh_token: refreshToken,
    });

    this.setAccessToken(response.data.access_token);
  }

  public setAccessToken(token?: string | null) {
    if(!token) return;
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  public setRefreshToken(token?: string | null) {
    if(!token) return;
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  public clearTokens() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  public async request<T>(config: AxiosRequestConfig, schema: ZodSchema<T>): Promise<T> {
    const response = await this.instance(config);
    return schema.parse(response.data);
  }
}
